import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {Hub} from 'yougile-model'
import {ErrorBoundary} from '@components/common/error-boundary/error-boundary'
import {MoveToBody} from '@components/move-to-body'
import {cn} from '@containers/html-utils'
import {CodeFiles} from './code-files/code-files'
import {
  ScriptsApiNavContext,
  useScriptsApiNavStore
} from './scripts-api-nav-store'
import {ScriptsCodeContext, useScriptsCodeStore} from './scripts-code-store'
import {ScriptsObjNavContext} from './scripts-obj-nav-store'
import {ScriptsContext, ScriptsStore, useScriptsStore} from './scripts-store'
import {ToolsApi} from './tools-api/tools-api'
import {ToolsHelp} from './tools-help/tools-help'
import {ToolsObj} from './tools-obj/tools-obj'

export const Scripts = () => {
  const [store] = useState(() => new ScriptsStore())
  useEffect(() => {
    store.init()
    return () => store.dispose()
  }, [store])

  return (
    <ErrorBoundary
      silent
      onError={e => logger.error('scripts error ' + e.message)}
    >
      <ScriptsContext.Provider value={store}>
        <ScriptsCodeContext.Provider value={store.globalState.codeStore}>
          <ScriptsObjNavContext.Provider value={store.globalState.objNavStore}>
            <ScriptsApiNavContext.Provider
              value={store.globalState.apiNavStore}
            >
              <ScriptsBase />
            </ScriptsApiNavContext.Provider>
          </ScriptsObjNavContext.Provider>
        </ScriptsCodeContext.Provider>
      </ScriptsContext.Provider>
    </ErrorBoundary>
  )
}

const ScriptsBase = observer(() => {
  const [state, store] = useScriptsStore()
  const [codeState, codeStore] = useScriptsCodeStore()
  const [apiNavState] = useScriptsApiNavStore()

  return (
    <MoveToBody className={cn(!state.scriptsOpenSaved && 'hidden')}>
      <div className={cn('scr', store.isFullscreen() && 'fullscreen')}>
        <div className="scr-head">
          {Lang.msg.company} “{state.companyName}”
          <div className="scr-head-btn">
            {state.fullscreenAllowed && (
              <a
                href="/team/api-editor"
                target="_blank"
                title="Fullscreen"
                rel="noopener noreferrer"
              >
                <i className="fa fa-window-maximize scr-fullscreen-icon" />
              </a>
            )}
            <i className="fa fa-times scr-close-icon" onClick={store.close} />
          </div>
        </div>

        <div className="scr-body">
          <div className={cn('scr-code', codeState.openedTab && 'with-editor')}>
            <div className="scr-code-tabs">
              <div
                className={cn('scr-tab', !codeState.openedTab && 'active')}
                onClick={() => (codeState.openedTab = undefined)}
              >
                <i className="fa fa-files-o" /> {Lang.msg.scripts}
              </div>
              {codeState.tabs
                .filter(tab => !(tab as Hub).deleted)
                .map(tab => (
                  <div
                    key={tab.id}
                    className={cn(
                      `scr-tab`,
                      tab.id === codeState.openedTab?.id && 'active',
                      (tab as Hub).dataType === 'Hub' &&
                        codeState.changed[tab.id] &&
                        'modified'
                    )}
                    onClick={() => codeStore.openTab(tab)}
                  >
                    <span className="sct-tab-name">
                      {codeStore.getName(tab)}
                    </span>
                    <i
                      className="fa fa-times scr-tab-close"
                      onClick={e => {
                        codeStore.closeTab(tab)
                        e.stopPropagation()
                      }}
                    />
                  </div>
                ))}
            </div>

            <CodeFiles />
          </div>

          <div className="scr-tools">
            <div className="scr-tools-tabs">
              <div
                className={`scr-tab ${
                  state.openedNav === 'api' ? 'active' : ''
                }`}
                onClick={() => store.setOpenedNav('api')}
              >
                {Lang.msg.apiNavigator}
              </div>
              <div
                className={`scr-tab ${
                  state.openedNav === 'obj' ? 'active' : ''
                }`}
                onClick={() => store.setOpenedNav('obj')}
              >
                {Lang.msg.objectNavigator}
              </div>
              <div
                className={`scr-tab ${
                  state.openedNav === 'help' ? 'active' : ''
                }`}
                onClick={() => store.setOpenedNav('help')}
              >
                {Lang.msg.apiHelp}
              </div>
            </div>

            <div className="scr-tools-cnt">
              {state.openedNav === 'api' && <ToolsApi />}
              {state.openedNav === 'obj' && <ToolsObj />}
              {state.openedNav === 'help' && <ToolsHelp />}
            </div>

            <div
              className={`scr-api-nav-hint ${
                apiNavState.hovered ? 'expanded' : ''
              }`}
              style={{display: state.openedNav === 'api' ? 'block' : 'none'}}
            >
              <div className="scr-api-nav-hint-arrow" />
              <div
                className="scr-api-nav-hint-cnt"
                style={{display: apiNavState.hovered ? 'block' : 'none'}}
              >
                <h3 className="scr-api-hav-head">
                  {apiNavState.hovered?.parent && (
                    <span>
                      {apiNavState.hovered.parent.isVar
                        ? `(${apiNavState.hovered.parent.display})`
                        : apiNavState.hovered.parent.display}
                      .
                    </span>
                  )}
                  {apiNavState.hovered?.display}
                </h3>
                <p className="scr-api-nav-desc">
                  {apiNavState.hovered?.describe()}
                </p>
                <div>
                  <div
                    className="_link"
                    onClick={() =>
                      store.insertIntoEditor(apiNavState.hovered.code)
                    }
                  >
                    {Lang.msg.editInScript}
                  </div>
                </div>
                {apiNavState.hovered?.example && (
                  <div className="scr-api-nav-example">
                    {Lang.msg.usageExample}:
                    <div className="scr-api-nav-example-cnt">
                      <div
                        style={{
                          fontFamily: 'Menlo, Monaco, Courier New, monospace',
                          whiteSpace: 'pre-wrap'
                        }}
                        data-lang="text/javascript"
                      >
                        {apiNavState.hovered.example}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MoveToBody>
  )
})
